"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { CrossIcon } from "../../icons/index.js";
import renderIcon from "../../icons/render-icon.js";
import makeStyles from "../../theming/make-styles.js";
import { IconButton } from "../button/index.js";
import makePadding from "../utils/make-padding.js";
function fontStyle(size, _a) {
    var { typography } = _a, theme = __rest(_a, ["typography"]);
    const style = {
        small: {
            height: theme.size(24),
            fontSize: typography.small.fontSize,
        },
        medium: {
            height: theme.size(32),
            fontSize: typography.body.fontSize,
        },
    };
    return style[size];
}
const paddings = {
    small: { x: 9, y: 2 },
    medium: { x: 12, y: "xs" },
};
const margins = {
    small: 6,
    medium: "sm",
};
const avatarSizes = {
    small: 18,
    medium: 24,
};
const iconSizes = {
    small: 14,
    medium: undefined,
};
const useStyles = makeStyles()((theme, { avatar, closable, color, disabled, hoverable, label, size, variant }) => {
    var _a, _b;
    const padding = makePadding(paddings, theme);
    const palette = theme.palette(color).tag[variant];
    return {
        avatar: {
            "width": avatarSizes[size],
            "height": avatarSizes[size],
            "marginRight": label ? theme.spacing(margins[size]) : undefined,
            "& > div": {
                fontSize: theme.size(avatarSizes[size] / 2),
            },
        },
        close: {
            "padding": 0,
            "marginLeft": theme.spacing(margins[size]),
            "&:focus:not(:disabled), &:active:not(:disabled)": {
                boxShadow: ((_b = (_a = palette.closeButton) === null || _a === void 0 ? void 0 : _a.active) === null || _b === void 0 ? void 0 : _b.boxShadowColor) && theme.outline(palette.closeButton.active.boxShadowColor),
            },
        },
        icon: {
            marginRight: label ? theme.spacing(margins[size]) : undefined,
        },
        tag: Object.assign(Object.assign({ "display": "inline-flex", "alignItems": "center", "padding": padding(size, theme.size(1)), "paddingRight": closable ? theme.spacing(3) : undefined, "paddingLeft": avatar ? theme.spacing(3) : undefined, "color": palette.color, "backgroundColor": palette.backgroundColor, "border": `${theme.size(1)} solid ${palette.borderColor}`, "borderRadius": theme.size(9999), "cursor": hoverable && !disabled ? "pointer" : undefined, "outline": 0, "userSelect": disabled || hoverable ? "none" : undefined, "lineHeight": 1, "verticalAlign": "middle", "whiteSpace": "nowrap", "&:hover": hoverable &&
                !disabled && Object.assign({}, palette.hover), 
            // TODO :has selector supported by FF v121
            // "&:hover:not(:has(*:hover))": hoverable &&
            //   !disabled && {
            //     ...palette.hover,
            //   },
            "&:focus": hoverable &&
                !disabled && {
                borderColor: palette.focus.borderColor,
                boxShadow: theme.outline(palette.focus.boxShadowColor),
            }, "&:active": hoverable &&
                !disabled && Object.assign({}, palette.active), "& svg": {
                width: iconSizes[size],
                height: iconSizes[size],
            } }, (disabled && palette.disabled)), fontStyle(size, theme)),
    };
});
export const closeButtonDefaultVariants = {
    "filled": "filled",
    "outline": "link",
    "faded": "link",
    "faded-outline": "link",
};
export const DefaultCloseButton = React.forwardRef(function DefaultCloseButton(_a, ref) {
    var { tagVariant } = _a, props = __rest(_a, ["tagVariant"]);
    return _jsx(IconButton, Object.assign({ ref: ref, icon: CrossIcon, round: true, shadow: false, size: "small", variant: closeButtonDefaultVariants[tagVariant] }, props));
});
export const Tag = React.forwardRef(function Tag(_a, ref) {
    var { avatar, className, closable = false, closeButton: CloseButton = DefaultCloseButton, color = "primary", disabled = false, hoverable = false, icon, label, size = "medium", variant = "filled", onClose } = _a, props = __rest(_a, ["avatar", "className", "closable", "closeButton", "color", "disabled", "hoverable", "icon", "label", "size", "variant", "onClose"]);
    const { classes, cx } = useStyles({ avatar, closable, color, disabled, hoverable, label, size, variant });
    return (_jsxs("div", Object.assign({ ref: ref, className: cx(classes.tag, className), "data-component": "tag", tabIndex: hoverable ? 0 : undefined }, props, { children: [avatar &&
                React.cloneElement(avatar, {
                    className: cx(classes.avatar, avatar.props.className),
                    color: avatar.props.color || color,
                }), renderIcon(icon, classes.icon), label, closable && _jsx(CloseButton, { className: classes.close, color: color, disabled: disabled, tagVariant: variant, onClick: onClose })] })));
});
export default Tag;
//# sourceMappingURL=tag.js.map